import type { DbEvent, DbEventKind } from '@apm/contracts';
import { currentQuery, routeHref } from '../router';
import { DB_EVENT_LABEL, TIP } from '../terms';
import { formatDuration, formatFull, formatStamp } from '../time';
import { Cell, Chip, type ChipTone, type Column, Row, Table } from '../ui';

/**
 * DB 사건 표 (9회차 결정 4). DB 감시 탭과 요청 상세가 같은 표를 쓴다.
 * 요청 번호 열은 상세 화면에서는 볼 이유가 없어 `showTrace`로 끈다.
 */

const KIND_TONE: Record<DbEventKind, ChipTone> = {
  blocked: 'error',
  lock_wait: 'fail',
  idle_in_transaction: 'sse',
  long_query: 'http',
};

export function DbEventTable({
  rows,
  showTrace = true,
}: {
  rows: readonly DbEvent[];
  showTrace?: boolean;
}) {
  const columns: Column[] = [
    { label: '시각', width: 'w-44' },
    { label: '종류', width: 'w-52' },
    { label: '지속', width: 'w-24' },
    { label: 'pid', width: 'w-24' },
    { label: 'SQL · 잠금' },
  ];
  if (showTrace) columns.push({ label: '요청', width: 'w-44', title: TIP.dbTraceComment });

  return (
    <Table columns={columns} caption="DB 사건" minWidth="min-w-[900px]">
      {rows.map((row) => (
        <Row key={row.id}>
          <Cell nowrap className="tabular" title={formatFull(row.startedAt)}>
            {formatStamp(row.startedAt)}
          </Cell>
          <Cell>
            <Chip tone={KIND_TONE[row.kind]}>{DB_EVENT_LABEL[row.kind]}</Chip>
            {row.blockedByPid !== undefined ? (
              <span className="ml-2 text-muted">pid {row.blockedByPid}</span>
            ) : null}
          </Cell>
          <Cell mono nowrap className="tabular">
            {formatDuration(row.durationMs ?? 0)}
          </Cell>
          <Cell mono tone="muted" className="tabular">
            {row.pid}
          </Cell>
          <Cell mono title={row.queryHead} className="max-w-[420px] truncate">
            {row.queryHead === '' ? '-' : row.queryHead}
            {row.lockType ? (
              <span className="ml-2 text-muted">
                [{row.lockType}
                {row.relation ? ` ${row.relation}` : ''}]
              </span>
            ) : null}
          </Cell>
          {showTrace ? (
            <Cell>
              <TraceLink traceId={row.traceId ?? row.blockedByTraceId} />
            </Cell>
          ) : null}
        </Row>
      ))}
    </Table>
  );
}

function TraceLink({ traceId }: { traceId?: string }) {
  if (traceId === undefined) return <span className="text-muted">요청 정보 없음</span>;
  return (
    <a
      href={routeHref({ name: 'detail', traceId, from: 'dbevents', back: currentQuery() })}
      title={traceId}
      className="font-mono text-link no-underline hover:underline"
    >
      {traceId.slice(0, 8)}
    </a>
  );
}
